import { Dispatch, FC, Fragment, memo, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Theme, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    BookingCalendarSelection,
    BookingDeviceModalData,
    Device,
    DevicesResponse,
    DevicesType,
    SelectedBookingId,
    SelectedDeviceId,
} from '../types';
import BookingCalendarItem from './BookingCalendarItem';
import { useSelector } from 'react-redux';
import { selectGetBookingsStatus } from '../store/selectors';
import BookingCalendarItemFallback from './BookingCalendarItemFallback';
import BookingDeviceModal from './BookingDeviceModal';
import { RequestStatusEnum } from '@shared/types';
import { generateDatesFromRange, generateSlotsIndexes, isPastCalendarItem } from '@pages/booking/utils';
import { isSameDate } from '@shared/utils/utilsOld';
import { useBookingRangeContext } from '@pages/booking/contexts/BookingRangeContext';
import BookingTimeLine from './BookingTimeLine';
import { useCurrentTime } from '../hooks/useCurrentTime';

const hours = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);

const CellStyle = {
    p: 1,
    height: '40px',
    textAlign: 'center',
    fontWeight: 600,
};
const HourCellWidth = 48;
const MinSlotWidth = 20;

const getHeaderCellStyle = (theme: Theme) => ({
    position: 'sticky',
    top: 0,
    zIndex: 5,
    background: theme.palette.background.paper,
    whiteSpace: 'nowrap',
    border: '1px solid var(--border-color)',
    borderRight: 'none',
});

const getDayDividerStyle = (theme: Theme) => {
    return {
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            width: '1px',
            backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.32)' : 'rgba(255, 255, 255, 0.32)',
            zIndex: 1,
            left: '100%',
        },
    };
};

interface BookingCalendarViewProps {
    devices: DevicesResponse | null;
    selectedDeviceId: SelectedDeviceId;
    selectedBookingId: SelectedBookingId;
    setSelectedBookingId: (newBookingId: SelectedBookingId) => void;
    modalData: BookingDeviceModalData | null;
    setModalData: Dispatch<SetStateAction<BookingDeviceModalData | null>>;
}

const BookingCalendarView: FC<BookingCalendarViewProps> = ({
    devices,
    selectedDeviceId,
    selectedBookingId,
    setSelectedBookingId,
    modalData,
    setModalData,
}) => {
    const { i18n } = useTranslation();
    const { range } = useBookingRangeContext();
    const theme = useTheme();
    const [bookingsSelection, setBookingsSelection] = useState<BookingCalendarSelection>({}); // shows which items will be booked
    const getBookingsStatus = useSelector(selectGetBookingsStatus);
    const currentHour = useCurrentTime({ getValue: () => new Date().getHours(), intervalMs: 3_600_000 });
    const selectedDevice = useMemo(() => {
        if (!devices) {
            return;
        }

        for (const value of Object.values(devices)) {
            const selectedDevice = (value as Array<Device>).find((device) => device.pk === selectedDeviceId);

            if (!selectedDevice) {
                continue;
            }

            return selectedDevice;
        }
    }, [devices, selectedDeviceId]);
    const isItemWithSlots =
        selectedDevice && (selectedDevice.type === DevicesType.IXIA || selectedDevice.type === DevicesType.SPIRENT);
    const currentHourRef = useRef<HTMLElement>();

    useEffect(() => {
        // Scroll to current hour to show more actual info
        currentHourRef.current && currentHourRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);

    const dates = useMemo(() => {
        return generateDatesFromRange(range);
    }, [range]);

    const isCurrentWeek = useMemo(() => {
        const today = new Date();
        return dates.some((date) => isSameDate(date, today));
    }, [dates]);

    const slotsArray = useMemo(() => {
        if (!isItemWithSlots) {
            return;
        }

        return generateSlotsIndexes(selectedDevice.slots_count);
    }, [isItemWithSlots, selectedDevice]);

    return (
        <Box
            sx={{
                flex: 1,
                overflowY: 'auto',
                color: 'var(--text-primary)',
            }}
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: `${HourCellWidth}px repeat(${dates.length}, 1fr)`,
                }}
            >
                <Box
                    sx={{
                        ...CellStyle,
                        ...getHeaderCellStyle(theme),
                        width: `${HourCellWidth}px`,
                    }}
                />
                {dates.map((date, dateIndex, dateArr) => (
                    <Box
                        key={date.toISOString()}
                        sx={{
                            ...CellStyle,
                            ...getHeaderCellStyle(theme),
                            borderRight: dateIndex === dateArr.length - 1 ? '1px solid var(--border-color)' : 'none',
                            borderTop: isSameDate(date, new Date()) ? `4px solid ${theme.palette.primary.main}` : '',
                        }}
                    >
                        {`${date.getDate()} ${new Intl.DateTimeFormat(i18n.language, { weekday: 'long' }).format(date)}`}
                    </Box>
                ))}
                {hours.map((hour, hourIndex) => (
                    <Fragment key={hour}>
                        <Box
                            ref={
                                hour === new Date().getHours().toString().padStart(2, '0') + ':00'
                                    ? currentHourRef
                                    : undefined
                            }
                            sx={{
                                ...CellStyle,
                                width: `${HourCellWidth}px`,
                                border: '1px solid var(--border-color)',
                                borderTop: 'none',
                                borderRight: 'none',
                                position: 'sticky',
                                left: 0,
                                zIndex: 4,
                                background: theme.palette.background.paper,
                            }}
                        >
                            {hour}
                        </Box>
                        {dates.map((date, dateIndex, dateArr) => {
                            const dateTime = new Date(date);
                            const [hours, minutes] = hour.split(':').map(Number);
                            dateTime.setHours(hours, minutes, 0, 0);
                            const multiplier = isItemWithSlots ? selectedDevice.slots_count : 1; // for correct tabIndex for devices with slots
                            const tabIndex = dateIndex * multiplier * 24 + hourIndex * multiplier + 1; // 24 - hours, 1 for correct start tabIndex
                            const isCurrentHour = dateTime.getHours() === currentHour;

                            return (
                                <Box
                                    key={dateTime.toISOString()}
                                    sx={{
                                        ...CellStyle,
                                        position: 'relative',
                                        border: 'none',
                                        borderRight:
                                            dateIndex === dateArr.length - 1 ? '1px solid var(--border-color)' : 'none',
                                        p: 0,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        minWidth: isItemWithSlots
                                            ? `${selectedDevice.slots_count * MinSlotWidth}px`
                                            : '150px',
                                        ...(dateIndex !== dateArr.length - 1 && getDayDividerStyle(theme)),
                                    }}
                                >
                                    {isCurrentWeek && isCurrentHour && <BookingTimeLine />}
                                    {getBookingsStatus === RequestStatusEnum.Success &&
                                        (isItemWithSlots && slotsArray ? (
                                            slotsArray.map((slotIndex) => (
                                                <BookingCalendarItem
                                                    key={slotIndex}
                                                    tabIndex={
                                                        isPastCalendarItem(dateTime, false)
                                                            ? undefined
                                                            : tabIndex + slotIndex
                                                    }
                                                    dateTime={dateTime}
                                                    isInSelection={
                                                        bookingsSelection[`${dateTime.toISOString()}_${slotIndex}`]
                                                    }
                                                    setBookingsSelection={setBookingsSelection}
                                                    setModalData={setModalData}
                                                    selectedDevice={selectedDevice}
                                                    selectedBookingId={selectedBookingId}
                                                    setSelectedBookingId={setSelectedBookingId}
                                                    slotIndex={slotIndex}
                                                    slotsLength={selectedDevice.slots_count}
                                                />
                                            ))
                                        ) : (
                                            <BookingCalendarItem
                                                tabIndex={isPastCalendarItem(dateTime, false) ? undefined : tabIndex}
                                                dateTime={dateTime}
                                                isInSelection={bookingsSelection[`${dateTime.toISOString()}`]}
                                                setBookingsSelection={setBookingsSelection}
                                                setModalData={setModalData}
                                                selectedDevice={selectedDevice}
                                                selectedBookingId={selectedBookingId}
                                                setSelectedBookingId={setSelectedBookingId}
                                                isFirstDayAndFirstHour={dateIndex === 0 && hourIndex === 0}
                                            />
                                        ))}
                                    {getBookingsStatus !== RequestStatusEnum.Success && <BookingCalendarItemFallback />}
                                </Box>
                            );
                        })}
                    </Fragment>
                ))}
            </Box>
            {modalData && (
                <BookingDeviceModal
                    selectedDevice={selectedDevice}
                    setBookingsSelection={setBookingsSelection}
                    modalData={modalData}
                    setModalData={setModalData}
                    setSelectedBookingId={setSelectedBookingId}
                />
            )}
        </Box>
    );
};

export default memo(BookingCalendarView);
